import { Button, Group, Stack, Text, TextInput } from '@mantine/core';
import { closeModal } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { handleSubmitError } from '@utils/forms';

import {
  CreateMedicalSpecialityRequest,
  MedicalSpeciality,
  UpdateMedicalSpecialityRequest,
} from '@interfaces/medicalSpecialities.interface';

import {
  useCreateMedicalSpecialityMutation,
  useUpdateMedicalSpecialityMutation,
} from '@api/medicalSpecialities.api';

import useFormEnhanced from '@hooks/useFormEnhanced';

export const UPSERT_MEDICAL_SPECIALITY_FORM_MODAL_ID =
  'upsertMedicalSpeciality';

interface UpsertMedicalSpecialityFormProps {
  medicalSpeciality?: MedicalSpeciality;
}

export default function UpsertMedicalSpecialityForm({
  medicalSpeciality,
}: UpsertMedicalSpecialityFormProps) {
  const initialValues = {
    name: medicalSpeciality?.name || '',
  };

  const form = useFormEnhanced({
    initialValues,
    normalizeFields: {},
  });

  const [createMedicalSpeciality, { isLoading: isLoadingCreate }] =
    useCreateMedicalSpecialityMutation();
  const [updateMedicalSpeciality, { isLoading: isLoadingUpdate }] =
    useUpdateMedicalSpecialityMutation();

  const onCancelClick = () => {
    closeModal(UPSERT_MEDICAL_SPECIALITY_FORM_MODAL_ID);
  };

  const onSubmit = async (values: typeof initialValues) => {
    try {
      if (medicalSpeciality) {
        const data: UpdateMedicalSpecialityRequest = {
          id: medicalSpeciality.id,
          body: {
            name: values.name,
          },
        };

        await updateMedicalSpeciality(data).unwrap();

        notifications.show({
          title: 'Specialità medica modificata',
          message: 'Specialità medica modificata con successo.',
        });
      } else {
        const data: CreateMedicalSpecialityRequest = {
          name: values.name,
        };

        await createMedicalSpeciality(data).unwrap();

        notifications.show({
          title: 'Specialità medica creata',
          message: 'Specialità medica creata con successo.',
        });
      }

      closeModal(UPSERT_MEDICAL_SPECIALITY_FORM_MODAL_ID);
    } catch (e) {
      handleSubmitError(e, form);
    }
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="sm">
        <TextInput
          data-autofocus
          required
          label="Nome"
          {...form.getInputProps('name')}
        />
      </Stack>

      {form.errors.general && (
        <Text c="red" size="xs">
          {form.errors.general}
        </Text>
      )}

      <Group justify="right" mt="xl">
        <Button
          onClick={onCancelClick}
          variant="default"
          loading={isLoadingCreate || isLoadingUpdate}
        >
          Annulla
        </Button>
        <Button type="submit" loading={isLoadingCreate || isLoadingUpdate}>
          Conferma
        </Button>
      </Group>
    </form>
  );
}
